import React, { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';

/**
 * Sandbox impersonation landing page.
 *
 * Opened in a NEW TAB from /admin/seed's Sandbox Launcher. Reads the
 * impersonation JWT + handle from the query string and drops them into
 * `sessionStorage` — which is scoped to THIS tab only. The admin's
 * original session in localStorage stays untouched, so:
 *   • Tab #1  (admin console): unchanged
 *   • Tab #2  (opened as @test_fan_1): sees @test_fan_1
 *   • Tab #3  (opened as @test_fan_3): sees @test_fan_3
 * All simultaneously, no incognito windows needed.
 *
 * api.js already prefers `sessionStorage.clanster_access_token` over the
 * localStorage variant (see api.js line ~14), so the switch is automatic.
 *
 * ⚠️ This route is intentionally not admin-guarded server-side — anyone
 * with a valid JWT in the query string is already authenticated by
 * definition. Server-side, /admin/seed/users/{id}/impersonate is the only
 * way to mint such a token and requires admin auth.
 */
export default function Impersonate() {
  const [params] = useSearchParams();
  const [ready, setReady] = useState(false);
  const [target, setTarget] = useState('/home');

  useEffect(() => {
    const token = params.get('token');
    const handle = params.get('handle') || '';
    const next = params.get('next') || '/home';
    if (!token) {
      setTarget('/login');
      setReady(true);
      return;
    }
    try {
      // Per-tab identity. Do NOT touch localStorage — admin session lives there.
      sessionStorage.setItem('clanster_access_token', token);
      sessionStorage.setItem('sandbox_handle', handle);
      // Tag the tab so a small badge can render in AppShell if we want later.
      document.title = `Sandbox · @${handle}`;
    } catch { /* storage blocked — will just fall through */ }
    setTarget(next);
    setReady(true);
  }, [params]);

  if (!ready) return null;
  return <Navigate to={target} replace />;
}
